import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Table, Button, Pagination, Alert, Form } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { DeleteModal } from '../../../Common/DeleteModal';
import { usePagination } from '../../../hooks/usePagination';
import { useDebounce } from '../../../hooks/useDebounce';
import { 
  listFeatures, 
  deleteFeature, 
  Feature 
} from '../../../api/featureManagement/featureService';
import FeatureFormModal from './FeatureFormModal';
import { getImageUrl } from '../../../utils/imageUrl';
import { PermissionWrapper } from '../../../components/PermissionWrapper/PermissionWrapper';
import '../FeatureManagement.css';
import './Features.css';

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'community', label: 'Community' },
  { value: 'payment', label: 'Payment' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ENABLED', label: 'Enabled' },
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'KILLED', label: 'Killed' },
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const FeaturesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.Login);
  const clientId = process.env.REACT_APP_CLIENT_ID || user?.clientId || '';
  
  // Pagination
  const {
    page,
    pageSize,
    totalPages,
    totalCount,
    setPage,
    setPageSize,
    setTotalPages,
    setTotalCount,
    handlePageChange
  } = usePagination(1, 10);

  // State
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [scopeFilter, setScopeFilter] = useState<{ value: string; label: string }>(SCOPE_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState<{ value: string; label: string }>(STATUS_OPTIONS[0]);
  
  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Feature | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);

  // Fetch features
  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        // clientId will be added automatically by the interceptor
        // Don't send page/pageSize to get all features
        // The API will return all matching features
        // Don't send name parameter for search - we'll filter client-side
      };
      
      if (scopeFilter.value !== 'all') {
        params.scope = scopeFilter.value;
      }
      
      if (statusFilter.value !== 'all') {
        params.status = statusFilter.value;
      }

      const response = await listFeatures(params);
      let allFeatures = response.paginatedResults || [];

      // Apply client-side search filtering by feature name only
      if (debouncedSearch.trim()) {
        const searchLower = debouncedSearch.toLowerCase().trim();
        allFeatures = allFeatures.filter(feature =>
          (feature.name || '').toLowerCase().includes(searchLower)
        );
      }
      
      // Calculate pagination from filtered data
      const totalCount = allFeatures.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      setTotalPages(totalPages);
      setTotalCount(totalCount);
      
      // Apply client-side pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedFeatures = allFeatures.slice(startIndex, endIndex);
      setFeatures(paginatedFeatures);
    } catch (err: any) {
      console.error('Error fetching features:', err);
      setError(err.message || 'Failed to load features. Please try again.');
      setFeatures([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, page, pageSize, debouncedSearch, scopeFilter, statusFilter]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, scopeFilter, statusFilter]);

  // Handlers
  const handleCreate = () => {
    setEditingFeature(null);
    setShowFormModal(true);
  };

  const handleView = (feature: Feature) => {
    navigate(`/feature-management/features/${feature.name}`, {
      state: { feature, clientId: feature.clientId, scope: feature.scope }
    });
  };

  const handleEdit = (feature: Feature) => {
    setEditingFeature(feature);
    setShowFormModal(true);
  };

  const handleDelete = (feature: Feature) => {
    setDeleteTarget(feature);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      await deleteFeature({
        name: deleteTarget.name,
        clientId: deleteTarget.clientId || clientId,
        scope: deleteTarget.scope
      });
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchFeatures();
    } catch (err: any) {
      console.error('Error deleting feature:', err);
      setDeleteError(err.message || 'Failed to delete feature. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const normalized = (status || '').toUpperCase();
    const labelMap: Record<string, string> = {
      ENABLED: 'Enabled',
      DISABLED: 'Disabled',
      KILLED: 'Killed',
    };
    const label = labelMap[normalized] || status;
    const modifier = normalized === 'ENABLED' ? 'enabled' : normalized === 'DISABLED' ? 'disabled' : normalized === 'KILLED' ? 'killed' : 'default';
    return <span className={`feature-status-pill ${modifier}`}>{label}</span>;
  };

  const getScopeBadge = (scope: string) => {
    const normalized = (scope || '').toLowerCase();
    const label = normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : scope;
    return <span className="feature-scope-pill">{label}</span>;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} active={page === 1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item key={p} active={page === p} onClick={() => handlePageChange(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} active={page === totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} features
        </div>
        <Pagination className="mb-0">
          <Pagination.First disabled={page === 1} onClick={() => handlePageChange(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => handlePageChange(page - 1)} />
          {items}
          <Pagination.Next disabled={page === totalPages} onClick={() => handlePageChange(page + 1)} />
          <Pagination.Last disabled={page === totalPages} onClick={() => handlePageChange(totalPages)} />
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="feature-management-page">
      {/* Breadcrumbs */}
      <div className="mb-3 mt-4" style={{ padding: '0 15px' }}>
        <Breadcrumbs
          items={[
            { label: 'Feature Management', onClick: () => navigate('/feature-management') },
            { label: 'Features', active: true }
          ]}
        />
      </div>

      {/* Header + Filters */}
      <div className='mt-4' style={{ padding: '0 15px', marginBottom: '24px' }}>
        <Row className="align-items-center justify-content-between g-3">
          {/* Left: Title */}
          <Col xs="12" md="auto">
            <h2 className="mb-0">Features</h2>
          </Col>

          {/* Right: Search + Filters + Button */}
          <Col xs="12" md="8">
            <Row className="g-2 justify-content-md-end align-items-center">
              <Col xs={12} md={5}>
                <Form.Control
                  type="text"
                  placeholder="Search features"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col xs={6} md={2}>
                <Select
                  classNamePrefix="react-select"
                  value={scopeFilter}
                  onChange={(option) => setScopeFilter(option || SCOPE_OPTIONS[0])}
                  options={SCOPE_OPTIONS}
                  isClearable={false}
                />
              </Col>
              <Col xs={6} md={2}>
                <Select
                  classNamePrefix="react-select"
                  value={statusFilter}
                  onChange={(option) => setStatusFilter(option || STATUS_OPTIONS[0])}
                  options={STATUS_OPTIONS}
                  isClearable={false}
                />
              </Col>
              <Col xs={12} md="auto" className="text-md-end d-flex align-items-stretch">
              <PermissionWrapper
                  anyOfPermissions={[
                    'feature_management.admin',
                    'feature_management.features.manage',
                  ]}
                  hideWhenDisabled
                >
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  className="w-100 h-100 d-flex align-items-center justify-content-center"
                >
                  Create New Feature +
                </Button>
                </PermissionWrapper>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ padding: '0 15px', marginBottom: '16px' }}>
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Table */}
      <div style={{ padding: '0 15px' }}>
        {loading ? (
          <ShimmerLoader type="table" rows={5} />
        ) : (
          <>
            <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '16px' }}>
              <Table responsive hover className="features-table mb-0">
                <thead>
                  <tr>
                    <th>Feature Name</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th>Client ID</th>
                    <th>Conditions</th>
                    <th>Created at</th>
                    <th>Updated at</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {features.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <div className="empty-state">
                          <i
                            className="ri-inbox-line"
                            style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}
                          ></i>
                          <h5>No features found</h5>
                          <p className="text-muted">
                            Get started by creating your first feature.
                          </p>
                          <PermissionWrapper
                            anyOfPermissions={[
                              'feature_management.admin',
                              'feature_management.features.manage',
                            ]}
                            hideWhenDisabled
                          >
                          <Button variant="primary" onClick={handleCreate}>
                            Create New Feature
                          </Button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    features.map((feature) => (
                      <tr
                        key={`${feature.name}-${feature.scope}-${feature.clientId}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleView(feature)}
                      >
                        <td>{feature.name}</td>
                        <td>{getScopeBadge(feature.scope)}</td>
                        <td>{getStatusBadge(feature.status)}</td>
                        <td>{feature.clientId}</td>
                        <td>
                          {feature.conditions && feature.conditions.length > 0
                            ? `${feature.conditions.length} condition(s)`
                            : '-'}
                        </td>
                        <td>
                          {feature.createdAt
                            ? new Date(feature.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '-'}
                        </td>
                        <td>
                          {feature.updatedAt
                            ? new Date(feature.updatedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '-'}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                        <PermissionWrapper
                            anyOfPermissions={[
                              'feature_management.admin',
                              'feature_management.features.manage',
                            ]}
                            hideWhenDisabled
                          >
                          <div className="feature-action-btns d-flex">
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleEdit(feature)}
                              title="Edit"
                            >
                              <img
                                src={getImageUrl('shared/assets/icons/edit-outlined.svg')}
                                alt="Edit"
                                style={{ width: 24, height: 24 }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const fallbackIcon = document.createElement('i');
                                  fallbackIcon.className = 'ph-pencil-simple';
                                  target.parentElement?.appendChild(fallbackIcon);
                                }}
                              />
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleDelete(feature)}
                              title="Delete"
                            >
                              <img
                                src={getImageUrl('shared/assets/icons/delete-outline.svg')}
                                alt="Delete"
                                style={{ width: 24, height: 24 }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const fallbackIcon = document.createElement('i');
                                  fallbackIcon.className = 'ph-trash';
                                  target.parentElement?.appendChild(fallbackIcon);
                                }}
                              />
                            </Button>
                          </div>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {renderPagination()}
            </div>
          </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        deleteModalFunction={confirmDelete}
        title="Delete This Feature?"
        message="Once deleted, this feature will be permanently removed. This action cannot be undone."
        confirmLabel="Confirm Delete"
        cancelLabel="Go Back"
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Feature Form Modal */}
      <FeatureFormModal
        show={showFormModal}
        handleClose={() => {
          setShowFormModal(false);
          setEditingFeature(null);
        }}
        onSuccess={() => {
          fetchFeatures();
        }}
        feature={editingFeature}
        clientId={clientId}
      />
    </Container>
  );
};

export default FeaturesListPage;
